/**
 * What the browser window shows during an immersive session, from the moment entry begins
 * through the whole session.
 *
 * During an immersive session the canvas mirrors the headset view, which is of no use to
 * anyone: the person who would look at it is wearing the headset. Covering it with a status
 * readout makes the window worth glancing at instead — who is in the room, what the frame
 * rate is, and a way out that does not involve finding the browser chrome in VR.
 *
 * **The cover comes down the instant the session ends, and reveals the splash — the room
 * selector — underneath it, not a second, dead-end screen of its own.** This used to keep the
 * cover up and swap Exit VR for Enter VR / Desktop mode, with no room picker and no way to
 * change rooms; the owner corrected that plan directly: *"I don't want an extra button. I want
 * to go back to the room selector page at /room"* — which is exactly what `Splash` already is
 * (`plan/session-exit-and-membership-fixes.md`, A1). `main.ts`'s own XR `onExit` handler is what
 * actually does this — `sessionScreen.hide()` plus `splash.reopen()` — so this class only ever
 * needs `show()`/`hide()` now; there is no "ended" state of its own to render, and no reenter or
 * desktop-mode button here to duplicate what the splash's own Enter VR / Desktop mode buttons
 * already do.
 *
 * A cover rather than hiding the canvas: the canvas owns the WebGL context the session
 * renders through, so removing it from layout risks the session itself.
 *
 * Also carries the per-capability status rows a headset session asked for directly — originally
 * "hand tracking, nothing happened" with no way to tell whether the Quest setting was off, the
 * browser declined the optional feature, or the app misconfigured it; hand tracking itself is
 * gone (2026-08-10, CLAUDE.md), and the same shape now covers "no controllers connected," a
 * session with nothing left to act with once hand tracking was the only fallback for that. See
 * `CapabilityState` for the vocabulary and `setCapability` for how a row gets there — main.ts is
 * what actually calls it, from data XrManager already computes (`onSetupIncomplete`,
 * `onBoundedFloorChanged`), not from any new detection added here.
 *
 * Also carries the model library's own boot-time load outcome (`setModelStatus`) — how many of
 * this build's known model-backed catalogue types actually resolved to a usable mesh. Added
 * because "the model failed to load" and "this type was never model-backed" are indistinguishable
 * from a deployed bundle without this: a tray whose preview quietly fell back to a procedural
 * silhouette looked no different from one that was always procedural, and confirming which had
 * happened meant grepping a deployed JS bundle by hand (see ModelLibrary.ts, EXPECTED_MODEL_KEYS,
 * and CLAUDE.md's ".glb has no default content type" note on the outage this already caused
 * once). Folded into the same capability list rather than a bespoke widget — same status-readout
 * language, and it is asked once at boot and never changes for the life of a session, the same
 * shape as every capability row before a session actually starts.
 */

import type { ModelLoadSummary } from '../room/ModelLibrary';

export interface SessionInfo {
  room: string;
  displayName: string;
}

export interface SessionStats {
  peers: string[];
  fps: number;
  nodes: number;
  connections: number;
}

/**
 * Four things that must not be blurred into each other:
 *
 * - `pending` — asked for, outcome not known yet. Covers both "before a session exists" and
 *   "session entry is in flight but hasn't resolved" — the two pre-resolution moments are
 *   different questions (what the browser claims to support vs. what it is about to decide),
 *   but neither has an answer yet, so both read the same way here.
 * - `ready` — granted. The feature was actually handed over to the live session.
 * - `unavailable` — requested, not granted. The *normal* outcome for an optional feature on
 *   plenty of setups (no guardian configured, for bounded-floor) — not an error, and must not
 *   look like one.
 * - `broken` — the client's own WebXR setup is missing this outright, independent of what any
 *   session goes on to grant or refuse. See `XrManager.onSetupIncomplete`.
 *
 * Reuses Splash's own check/dot vocabulary (index.html) rather than inventing a second visual
 * language, with `broken` as the one addition Splash has never needed: nothing there currently
 * has to say "the app itself is at fault" as opposed to "this isn't available right now."
 */
export type CapabilityState = 'pending' | 'ready' | 'unavailable' | 'broken';

export interface CapabilityLine {
  id: string;
  label: string;
  state: CapabilityState;
}

export class SessionScreen {
  private readonly root: HTMLElement;
  private readonly peersHost: HTMLElement;
  private readonly capabilitiesHost: HTMLElement;
  private readonly fpsHost: HTMLElement;
  private readonly contentHost: HTMLElement;
  private readonly headingHost: HTMLElement;
  private readonly exitButton: HTMLButtonElement;

  private readonly capabilities = new Map<string, CapabilityLine>();

  /** Set by the caller to end the session. */
  onExitRequested: (() => void) | null = null;

  constructor(host: HTMLElement) {
    this.root = document.createElement('div');
    this.root.className = 'session-screen';
    this.root.hidden = true;
    this.root.innerHTML = `
      <div class="session-panel">
        <h1><span class="live-dot"></span>in the headset</h1>
        <p class="session-heading"></p>
        <div class="session-grid">
          <div><span class="session-label">frame rate</span><span class="session-fps">—</span></div>
          <div><span class="session-label">room</span><span class="session-content">—</span></div>
        </div>
        <div class="session-peers">
          <span class="session-label">in this room</span>
          <ul></ul>
        </div>
        <div class="session-capabilities">
          <span class="session-label">optional features</span>
          <ul aria-live="polite"></ul>
        </div>
        <div class="session-actions">
          <button type="button" class="session-exit">Exit VR</button>
        </div>
        <p class="session-foot">
          The world is rendering in the headset. This window is only a readout.
        </p>
      </div>
    `;

    host.appendChild(this.root);

    this.headingHost = this.query('.session-heading');
    this.peersHost = this.query('.session-peers ul');
    this.capabilitiesHost = this.query('.session-capabilities ul');
    this.fpsHost = this.query('.session-fps');
    this.contentHost = this.query('.session-content');
    this.exitButton = this.query('.session-exit');

    this.exitButton.addEventListener('click', () => this.onExitRequested?.());
  }

  /** Shows the cover for a session that is entering or in progress. */
  show(info: SessionInfo): void {
    this.headingHost.textContent = `${info.displayName} in ${info.room}`;
    this.root.hidden = false;
  }

  /** Takes the cover down — on session end (`main.ts`'s XR `onExit`, which reveals the splash
   *  underneath), and for Desktop mode, which never covers the canvas at all. */
  hide(): void {
    this.root.hidden = true;
  }

  /**
   * Adds or updates a capability row. Order of first appearance is preserved, the same as
   * Splash's `setCheck` — and deliberately does not gate on `this.root.hidden` the way
   * `update()` below does: capability rows are meant to already be in place (most land at
   * boot, from XrManager.onSetupIncomplete, well before this screen is ever shown) rather
   * than starting empty and filling in only once the cover is up.
   */
  setCapability(id: string, label: string, state: CapabilityState): void {
    this.capabilities.set(id, { id, label, state });
    this.renderCapabilities();
  }

  /**
   * Reports the model library's boot-time load outcome as one more capability-shaped row —
   * "how many of this build's known model-backed types actually came up," so a preview or a
   * node quietly falling back to its procedural silhouette is something a person can confirm
   * from this window instead of a deployed bundle. `ready` when every expected key loaded;
   * `broken` otherwise, naming which are missing — an *unexpected* absence is a build/deploy
   * fact independent of what any session goes on to grant, the same category `broken` already
   * covers for a WebXR feature (see `CapabilityState`), not the "normal on plenty of setups"
   * shade `unavailable` carries for bounded-floor.
   */
  setModelStatus(summary: ModelLoadSummary): void {
    const missing = summary.expected - summary.loaded;

    const label =
      missing <= 0
        ? `models — ${summary.loaded}/${summary.expected} loaded`
        : `models — ${summary.loaded}/${summary.expected} loaded (missing: ${summary.failedKeys.join(', ')})`;

    this.setCapability('models', label, missing <= 0 ? 'ready' : 'broken');
  }

  update(stats: SessionStats): void {
    if (this.root.hidden) {
      return;
    }

    const fps = Math.round(stats.fps);
    this.fpsHost.textContent = `${fps} fps`;

    // Same thresholds as the in-headset panel, so the two never disagree about whether the
    // session is healthy.
    this.fpsHost.className = `session-fps ${fps >= 68 ? 'good' : fps >= 45 ? 'fair' : 'poor'}`;

    this.contentHost.textContent = `${stats.nodes} nodes · ${stats.connections} edges`;

    this.peersHost.replaceChildren(
      ...(stats.peers.length === 0
        ? [listItem('nobody else yet')]
        : stats.peers.map((peer) => listItem(peer))),
    );
  }

  dispose(): void {
    this.root.remove();
  }

  private renderCapabilities(): void {
    this.capabilitiesHost.replaceChildren(
      ...[...this.capabilities.values()].map((capability) => {
        const item = document.createElement('li');
        item.className = `check check-${capability.state}`;

        const dot = document.createElement('span');
        dot.className = 'dot';

        const label = document.createElement('span');
        label.textContent = capability.label;

        item.append(dot, label);
        return item;
      }),
    );
  }

  private query<T extends HTMLElement>(selector: string): T {
    const found = this.root.querySelector<T>(selector);

    if (!found) {
      throw new Error(`Session screen markup is missing ${selector}.`);
    }

    return found;
  }
}

function listItem(text: string): HTMLLIElement {
  const item = document.createElement('li');
  item.textContent = text;
  return item;
}
